import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/Button';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useDocumentTitle } from '@/hooks';
import { hasRouterHistory } from '@/stores/navigation-history.store';
import { AppRoutes } from '@/constants/routes';
import '@/styles/conntour.css';

export default function NotFoundPage() {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const navigate = useNavigate();
    useDocumentTitle('notFound');

    const handleBack = useCallback(() => {
        if (hasRouterHistory()) {
            navigate(-1);
        } else {
            navigate(AppRoutes.HOME);
        }
    }, [navigate]);

    return (
        <div className="h-screen overflow-hidden flex flex-col items-center justify-center px-4 conntour-bg relative">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/20" />

            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="w-full max-w-md"
            >
                <div className="bg-gray-200/5 backdrop-blur-3xl rounded-sm p-10 flex flex-col items-center">
                    <img
                        src="/icons/conntour_icon_white.svg"
                        alt="Conntour"
                        className="h-10 w-auto mb-6 drop-shadow-[0_0_8px_rgba(255,255,255,0.18)]"
                    />

                    <div className="text-[120px] font-bold leading-none text-grey-500 select-none mb-2 tracking-tight">
                        404
                    </div>

                    <h1 className="text-xl tracking-wide font-bold text-white/90 mb-3 text-center">
                        {t('notFound.title')}
                    </h1>

                    <p className="text-sm text-grey-300 text-center mb-8 max-w-xs">
                        {t('notFound.description')}
                    </p>

                    <Button
                        variant="primary"
                        size="large"
                        leadingIcon={
                            <ArrowLeftIcon className="w-4 h-4" weight="bold" />
                        }
                        onClick={handleBack}
                        className="w-full"
                    >
                        {t('notFound.goBack')}
                    </Button>
                </div>
            </motion.div>
        </div>
    );
}
